import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Info } from 'lucide-react';
import {
  DEV_PREFERENCE_FIELDS,
  DEV_PREFERENCE_FIELD_META,
  PROJECT_TYPE_LABEL,
  type DevPreferenceField,
  type DevPreferences,
} from '@allemnii-pulse/shared';
import { useDocument } from '../../lib/firestore.js';
import { api, ApiError } from '../../lib/api.js';
import { asDate, relativeTime } from '../../lib/time.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../components/ui/Card.js';
import { Button } from '../../components/ui/Button.js';
import { Input } from '../../components/ui/Input.js';
import { Textarea } from '../../components/ui/Textarea.js';
import { Label } from '../../components/ui/Label.js';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../components/ui/BaseTooltip.js';
import { UserName } from '../../components/UserName.js';

// A default this old is more likely to mislead than help — see the staleness note below.
const STALE_AFTER_MS = 90 * 24 * 60 * 60 * 1000;

type FormState = Record<DevPreferenceField, string> & { conventions: string };

function toFormState(prefs: DevPreferences | null): FormState {
  const state = {} as FormState;
  for (const field of DEV_PREFERENCE_FIELDS) state[field] = prefs?.[field] ?? '';
  state.conventions = prefs?.conventions ?? '';
  return state;
}

/**
 * Org settings card for the team's saved dev/deployment stack — pre-fills the TDD questionnaire
 * (see questionnairePrompt in the API) and can be edited here directly by an admin, or saved
 * inline from a questionnaire answer (see TddPage's "save as org default"). Visible to every org
 * member (the underlying doc is already readable by anyone in the org — see firestore.rules on
 * orgs/{orgId}/settings), but only admins/owners get the editable form.
 */
export function DevPreferencesCard({ orgId, canManage }: { orgId: string; canManage: boolean }) {
  const { data: prefs, loading } = useDocument<DevPreferences>(
    `orgs/${orgId}/settings/devPreferences`,
  );
  const [form, setForm] = useState<FormState>(() => toFormState(null));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Once the admin starts editing, further live updates to `prefs` (e.g. a teammate's inline
  // "save as org default" from the questionnaire) stop overwriting the in-progress form — they'd
  // otherwise silently clobber whatever's being typed.
  useEffect(() => {
    if (!dirty) setForm(toFormState(prefs));
  }, [prefs, dirty]);

  function setField(key: keyof FormState, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
    setDirty(true);
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.put(`/orgs/${orgId}/settings/dev-preferences`, form);
      setDirty(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save preferences.');
    } finally {
      setSaving(false);
    }
  }

  const hasAnyValue = prefs && (DEV_PREFERENCE_FIELDS.some((f) => prefs[f]) || prefs.conventions);
  const isStale = prefs?.updatedAt
    ? Date.now() - asDate(prefs.updatedAt).getTime() > STALE_AFTER_MS
    : false;

  // Fields aren't split up by project type (an org runs several types of project at once — see
  // DEV_PREFERENCE_FIELD_META in enums.ts) — instead the search matches against a field's label
  // or against the name of any project type it applies to, so typing "security" surfaces every
  // field tagged security_scan even when the field's own label doesn't mention it.
  const visibleFields = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return DEV_PREFERENCE_FIELDS;
    return DEV_PREFERENCE_FIELDS.filter((field) => {
      const meta = DEV_PREFERENCE_FIELD_META[field];
      if (meta.label.toLowerCase().includes(q)) return true;
      return meta.appliesTo.some((type) => PROJECT_TYPE_LABEL[type].toLowerCase().includes(q));
    });
  }, [search]);

  return (
    <Card>
      <form onSubmit={(e) => void handleSave(e)}>
        <CardHeader>
          <CardTitle>Dev preferences</CardTitle>
          <CardDescription>
            The team's default stack — used to pre-fill the TDD questionnaire on new projects.
          </CardDescription>
          {!loading && (
            <div className="flex flex-wrap items-center gap-1.5 pt-1 text-xs">
              {hasAnyValue && prefs?.updatedAt ? (
                <>
                  <span className={isStale ? 'font-medium text-danger' : 'text-muted'}>
                    {isStale ? 'Stale — ' : ''}
                    Updated {relativeTime(prefs.updatedAt)}
                  </span>
                  {prefs.updatedByUid && (
                    <span className="text-muted">
                      by <UserName uid={prefs.updatedByUid} />
                    </span>
                  )}
                </>
              ) : (
                <span className="text-muted">Never set.</span>
              )}
            </div>
          )}
        </CardHeader>

        <CardContent className="flex flex-col gap-3">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder='Search fields (e.g. "security", "hosting")'
            aria-label="Search dev preference fields"
          />

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {visibleFields.map((field) => {
              const meta = DEV_PREFERENCE_FIELD_META[field];
              return (
                <div key={field} className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-1">
                    <Label htmlFor={`devpref-${field}`}>{meta.label}</Label>
                    <Tooltip>
                      <TooltipTrigger
                        type="button"
                        aria-label={`About ${meta.label}`}
                        // Tooltips only open on hover, so phones get the description inline below.
                        className="text-muted outline-none hover:text-text max-md:hidden"
                      >
                        <Info className="h-3.5 w-3.5" />
                      </TooltipTrigger>
                      <TooltipContent side="top">{meta.description}</TooltipContent>
                    </Tooltip>
                  </div>
                  <p className="text-xs text-muted md:hidden">{meta.description}</p>
                  <Input
                    id={`devpref-${field}`}
                    value={form[field]}
                    maxLength={300}
                    disabled={!canManage}
                    placeholder={canManage ? 'Not set' : '—'}
                    onChange={(e) => setField(field, e.target.value)}
                  />
                  <div className="flex flex-wrap gap-1">
                    {meta.appliesTo.map((type) => (
                      <span
                        key={type}
                        className="rounded-full bg-muted/50 px-2 py-0.5 text-[11px] text-muted"
                      >
                        {PROJECT_TYPE_LABEL[type]}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
            {visibleFields.length === 0 && (
              <p className="text-sm text-muted sm:col-span-2">No fields match "{search}".</p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="devpref-conventions">Conventions</Label>
            <Textarea
              id="devpref-conventions"
              rows={4}
              value={form.conventions}
              maxLength={2000}
              disabled={!canManage}
              placeholder={canManage ? 'Coding conventions, folder structure, house rules…' : '—'}
              onChange={(e) => setField('conventions', e.target.value)}
            />
          </div>
          {error && <p className="text-sm text-danger">{error}</p>}
        </CardContent>

        {canManage && (
          <CardFooter className="justify-end">
            <Button type="submit" variant="default" disabled={saving || !dirty}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </CardFooter>
        )}
      </form>
    </Card>
  );
}
